import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Clock, User, Calendar, Loader2, AlertTriangle, MinusCircle, Plus, Wallet, ClipboardList } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function LeaveApprovals({ user }) {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [lopDialogOpen, setLopDialogOpen] = useState(false);
  const [directLopDialogOpen, setDirectLopDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  // Planning impact dialog (Flow B of Leave ↔ Planning interlock)
  const [planningImpactDialog, setPlanningImpactDialog] = useState({ open: false, loading: false, application: null, data: null });
  const [directLopData, setDirectLopData] = useState({
    employee_id: "",
    start_date: "",
    end_date: "",
    reason: "",
    is_half_day: false
  });
  // Encashment Requests
  const [encashRequests, setEncashRequests] = useState([]);
  const [encashLoading, setEncashLoading] = useState(true);
  const [encashRejectDialog, setEncashRejectDialog] = useState(false);
  const [selectedEncashRequest, setSelectedEncashRequest] = useState(null);
  const [encashRejectReason, setEncashRejectReason] = useState("");
  // Cancellation Requests
  const [cancelRequests, setCancelRequests] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(true);
  const [cancelRejectDialog, setCancelRejectDialog] = useState(false);
  const [selectedCancelRequest, setSelectedCancelRequest] = useState(null);
  const [cancelRejectReason, setCancelRejectReason] = useState("");
  // HR Force Cancel
  const [forceCancelDialog, setForceCancelDialog] = useState(false);
  const [forceCancelTarget, setForceCancelTarget] = useState(null);
  const [forceCancelReason, setForceCancelReason] = useState("");

  const isHrAdmin = user?.department === "HR" || user?.department === "Human Resources" ||
    user?.department === "Management" || user?.access_level === "Admin" || user?.access_level === "Manager";

  useEffect(() => {
    fetchPendingApprovals();
    fetchCancellationRequests();
    if (isHrAdmin) {
      fetchEmployees();
      fetchEncashmentRequests();
    }
  }, []);

  const fetchCancellationRequests = async () => {
    try {
      setCancelLoading(true);
      const res = await api.get("/leave/cancellation-requests/pending");
      setCancelRequests(res.data || []);
    } catch (e) {
      // silently fail
    } finally {
      setCancelLoading(false);
    }
  };

  const handleApproveCancellation = async (applicationId) => {
    if (!confirm("Approve this cancellation request? Balance will be restored to the employee.")) return;
    try {
      await api.put(`/leave/applications/${applicationId}/cancel-approve`);
      toast.success("Cancellation approved · balance restored");
      fetchCancellationRequests();
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve cancellation");
    }
  };

  const openCancelReject = (req) => {
    setSelectedCancelRequest(req);
    setCancelRejectReason("");
    setCancelRejectDialog(true);
  };

  const handleRejectCancellation = async () => {
    if (!cancelRejectReason.trim()) {
      toast.error("Rejection reason required");
      return;
    }
    try {
      await api.put(`/leave/applications/${selectedCancelRequest.id}/cancel-reject`, {
        rejection_reason: cancelRejectReason.trim()
      });
      toast.success("Cancellation rejected · leave remains approved");
      setCancelRejectDialog(false);
      fetchCancellationRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const openForceCancel = (app) => {
    setForceCancelTarget(app);
    setForceCancelReason("");
    setForceCancelDialog(true);
  };

  const submitForceCancel = async () => {
    if (!forceCancelReason.trim()) {
      toast.error("Reason required");
      return;
    }
    try {
      await api.put(`/leave/applications/${forceCancelTarget.id}/force-cancel`, {
        reason: forceCancelReason.trim()
      });
      toast.success("Leave force-cancelled · balance restored");
      setForceCancelDialog(false);
      fetchCancellationRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to force-cancel");
    }
  };

  const fetchEncashmentRequests = async () => {
    try {
      setEncashLoading(true);
      const res = await api.get("/leave/el-encashment-requests/all");
      setEncashRequests(res.data || []);
    } catch (e) {
      // silently fail
    } finally {
      setEncashLoading(false);
    }
  };

  const handleApproveEncashment = async (requestId) => {
    if (!confirm("Approve this encashment request? It will deduct EL days from the employee.")) return;
    try {
      await api.put(`/leave/el-encashment-requests/${requestId}/approve`);
      toast.success("Encashment request approved");
      fetchEncashmentRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const openEncashReject = (req) => {
    setSelectedEncashRequest(req);
    setEncashRejectReason("");
    setEncashRejectDialog(true);
  };

  const handleRejectEncashment = async () => {
    if (!encashRejectReason.trim()) {
      toast.error("Rejection reason required");
      return;
    }
    try {
      await api.put(`/leave/el-encashment-requests/${selectedEncashRequest.id}/reject`, {
        rejection_reason: encashRejectReason.trim()
      });
      toast.success("Encashment request rejected");
      setEncashRejectDialog(false);
      fetchEncashmentRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      const response = await api.get("/leave/approvals/pending");
      setPendingApprovals(response.data);
    } catch (error) {
      toast.error("Failed to fetch pending approvals");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/employees?status=active");
      setEmployees(res.data);
    } catch (e) {}
  };

  const handleApprove = async (applicationId) => {
    if (!confirm("Are you sure you want to approve this leave application?")) return;
    try {
      setProcessing(true);
      await api.put(`/leave/applications/${applicationId}/approve`);
      toast.success("Leave application approved successfully");
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve leave");
    } finally {
      setProcessing(false);
    }
  };

  const openPlanningImpact = async (application) => {
    setPlanningImpactDialog({ open: true, loading: true, application, data: null });
    try {
      const res = await api.get(`/planning/task-conflicts-for-leave?application_id=${application.id}`);
      setPlanningImpactDialog({ open: true, loading: false, application, data: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load planning impact");
      setPlanningImpactDialog({ open: false, loading: false, application: null, data: null });
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) { toast.error("Please provide a reason for rejection"); return; }
    try {
      setProcessing(true);
      await api.put(`/leave/applications/${selectedApplication.id}/reject`, {
        action: "reject", rejection_reason: rejectionReason
      });
      toast.success("Leave application rejected");
      setRejectDialogOpen(false);
      setRejectionReason("");
      setSelectedApplication(null);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject leave");
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkLop = async () => {
    try {
      setProcessing(true);
      await api.put(`/leave/applications/${selectedApplication.id}/mark-lop`);
      toast.success("Leave marked as Loss of Pay");
      setLopDialogOpen(false);
      setSelectedApplication(null);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as LOP");
    } finally {
      setProcessing(false);
    }
  };

  const handleDirectLop = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await api.post("/leave/direct-lop", directLopData);
      toast.success("Direct LOP record created");
      setDirectLopDialogOpen(false);
      setDirectLopData({ employee_id: "", start_date: "", end_date: "", reason: "", is_half_day: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create LOP");
    } finally {
      setProcessing(false);
    }
  };

  const isOverdue = (app) => {
    // Overdue: pending application from previous month and today is past 2nd working day of current month
    const now = new Date();
    const appDate = new Date(app.applied_at);
    const appMonth = appDate.getMonth();
    const appYear = appDate.getFullYear();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    if (appYear < currentYear || (appYear === currentYear && appMonth < currentMonth)) {
      // Past the 2nd working day of current month (simplification: day > 2)
      return now.getDate() > 2;
    }
    return false;
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      casual: "bg-blue-100 text-blue-800",
      sick: "bg-red-100 text-red-800",
      earned: "bg-green-100 text-green-800",
      maternity: "bg-orange-100 text-orange-800",
      lop: "bg-red-200 text-red-900"
    };
    return colors[type] || "bg-md3-surface-container text-md3-on-surface";
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">Leave Approvals</h1>
            <p className="text-md3-on-surface-variant">Review and approve team leave requests</p>
          </div>
          {isHrAdmin && (
            <Button
              onClick={() => setDirectLopDialogOpen(true)}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="direct-lop-btn"
            >
              <MinusCircle className="h-4 w-4 mr-2" />
              Record Direct LOP
            </Button>
          )}
        </div>

        <Tabs defaultValue="leave-requests" className="w-full">
          <TabsList className="bg-md3-surface-container">
            <TabsTrigger value="leave-requests" data-testid="leave-requests-tab">
              Leave Requests {pendingApprovals.length > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800">{pendingApprovals.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="cancellation-requests" data-testid="cancellation-requests-tab">
              Cancellation Requests {cancelRequests.length > 0 && <Badge className="ml-2 bg-orange-100 text-orange-800">{cancelRequests.length}</Badge>}
            </TabsTrigger>
            {isHrAdmin && (
              <TabsTrigger value="encashment-requests" data-testid="encashment-requests-tab">
                Encashment Requests {encashRequests.filter(r => r.status === "pending").length > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800">{encashRequests.filter(r => r.status === "pending").length}</Badge>}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="leave-requests" className="space-y-6 mt-4">
        <Card className="border-l-4 border-l-yellow-500 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Pending Approvals</p>
                <p className="text-md3-display-small">{pendingApprovals.length}</p>
                {pendingApprovals.some(isOverdue) && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {pendingApprovals.filter(isOverdue).length} overdue application(s)
                  </p>
                )}
              </div>
              <Clock className="h-12 w-12 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Pending Leave Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : pendingApprovals.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-4" />
                <p className="text-md3-on-surface-variant">No pending approvals</p>
              </div>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Application #</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Applied On</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingApprovals.map((app) => (
                    <TableRow key={app.id} className={isOverdue(app) ? "bg-red-50" : ""}>
                      <TableCell className="font-medium">
                        {app.application_number}
                        {isOverdue(app) && (
                          <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium" data-testid="overdue-badge">
                            Overdue
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{app.employee_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getLeaveTypeColor(app.leave_type)}>
                          {app.leave_type.charAt(0).toUpperCase() + app.leave_type.slice(1)}
                          {app.is_half_day && <span className="ml-1 text-xs">({app.half_day_period || "Half"})</span>}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-md3-body-small">
                          <Calendar className="h-3 w-3" />
                          <span>{app.start_date}</span>
                          {!app.is_half_day && <><span>→</span><span>{app.end_date}</span></>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">{app.total_days}</span> days
                        {app.is_lop && <Badge variant="outline" className="ml-2 text-xs text-red-600">LOP</Badge>}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{app.reason}</TableCell>
                      <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                        {new Date(app.applied_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="rounded-full text-xs"
                            onClick={() => openPlanningImpact(app)} data-testid={`planning-impact-btn-${app.id}`}
                            title="View planning impact during this leave">
                            <ClipboardList className="h-3 w-3 mr-1" /> Impact
                          </Button>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                            onClick={() => handleApprove(app.id)} disabled={processing} data-testid="approve-btn">
                            <CheckCircle className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                            onClick={() => { setSelectedApplication(app); setRejectDialogOpen(true); }} disabled={processing}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                          {isHrAdmin && !app.is_lop && (
                            <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 rounded-full text-xs"
                              onClick={() => { setSelectedApplication(app); setLopDialogOpen(true); }} disabled={processing} data-testid="mark-lop-btn">
                              Mark LOP
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="cancellation-requests" className="space-y-6 mt-4">
            <Card className="border-l-4 border-l-orange-500 shadow-md3-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Pending Cancellation Requests</p>
                    <p className="text-md3-display-small">{cancelRequests.length}</p>
                  </div>
                  <AlertTriangle className="h-12 w-12 text-orange-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-md3-surface-container shadow-md3-1 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large">Approved Leaves Awaiting Cancellation Decision</CardTitle>
              </CardHeader>
              <CardContent>
                {cancelLoading ? (
                  <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" /></div>
                ) : cancelRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-4" />
                    <p className="text-md3-on-surface-variant">No cancellation requests</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Leave Type</TableHead>
                        <TableHead>Date Range</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead>Original Reason</TableHead>
                        <TableHead>Cancel Reason</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cancelRequests.map((r) => (
                        <TableRow key={r.id} data-testid={`cancel-row-${r.id}`}>
                          <TableCell className="font-medium">{r.employee_name}</TableCell>
                          <TableCell>
                            <Badge className={getLeaveTypeColor(r.leave_type)}>
                              {r.leave_type?.charAt(0).toUpperCase() + r.leave_type?.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{r.start_date} → {r.end_date}</TableCell>
                          <TableCell className="text-right font-semibold">{r.total_days}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate text-orange-700" title={r.cancellation_reason}>{r.cancellation_reason}</TableCell>
                          <TableCell className="text-sm text-md3-on-surface-variant">
                            {r.cancellation_requested_at ? new Date(r.cancellation_requested_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                                onClick={() => handleApproveCancellation(r.id)} data-testid={`cancel-approve-${r.id}`}>
                                <CheckCircle className="h-3 w-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                                onClick={() => openCancelReject(r)} data-testid={`cancel-reject-${r.id}`}>
                                <XCircle className="h-3 w-3 mr-1" /> Reject
                              </Button>
                              {isHrAdmin && (
                                <Button size="sm" variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full text-xs"
                                  onClick={() => openForceCancel(r)} data-testid={`force-cancel-${r.id}`}>
                                  HR Force
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isHrAdmin && (
            <TabsContent value="encashment-requests" className="space-y-6 mt-4">
              <Card className="border-l-4 border-l-amber-500 shadow-md3-1">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-md3-body-small text-md3-on-surface-variant">Pending Encashment Requests</p>
                      <p className="text-md3-display-small">{encashRequests.filter(r => r.status === "pending").length}</p>
                    </div>
                    <Wallet className="h-12 w-12 text-amber-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container shadow-md3-1 rounded-md3-lg border-0">
                <CardHeader>
                  <CardTitle className="text-md3-title-large">All Encashment Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {encashLoading ? (
                    <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" /></div>
                  ) : encashRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <Wallet className="h-12 w-12 mx-auto text-amber-300 mb-4" />
                      <p className="text-md3-on-surface-variant">No encashment requests yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Applied</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead>Department</TableHead>
                          <TableHead className="text-right">Days</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">EL Balance</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {encashRequests.map((r) => (
                          <TableRow key={r.id} data-testid={`encash-approval-row-${r.id}`}>
                            <TableCell className="text-sm">{new Date(r.applied_at).toLocaleDateString()}</TableCell>
                            <TableCell className="font-medium">{r.employee_name}</TableCell>
                            <TableCell>{r.department}</TableCell>
                            <TableCell className="text-right font-semibold">{r.days}</TableCell>
                            <TableCell className="text-sm max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                            <TableCell className="text-right text-sm">{r.el_balance_at_request} <span className="text-xs text-md3-on-surface-variant">(thr {r.threshold_at_request})</span></TableCell>
                            <TableCell>
                              <Badge className={
                                r.status === "approved" ? "bg-green-100 text-green-800" :
                                r.status === "rejected" ? "bg-red-100 text-red-800" :
                                "bg-amber-100 text-amber-800"
                              }>{r.status}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-md3-on-surface-variant">
                              {r.status === "rejected" ? r.rejection_reason : (r.approved_by ? `by ${r.approved_by}` : "—")}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.status === "pending" && (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                                    onClick={() => handleApproveEncashment(r.id)} data-testid={`encash-approve-${r.id}`}>
                                    <CheckCircle className="h-3 w-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                                    onClick={() => openEncashReject(r)} data-testid={`encash-reject-${r.id}`}>
                                    <XCircle className="h-3 w-3 mr-1" /> Reject
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Cancellation Reject Dialog */}
        <Dialog open={cancelRejectDialog} onOpenChange={setCancelRejectDialog}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small">Reject Cancellation Request</DialogTitle>
            </DialogHeader>
            {selectedCancelRequest && (
              <div className="space-y-4">
                <div className="bg-md3-surface p-4 rounded-md3-lg space-y-1">
                  <p className="font-semibold">{selectedCancelRequest.employee_name}</p>
                  <p className="text-sm">{selectedCancelRequest.leave_type} · {selectedCancelRequest.total_days} days · {selectedCancelRequest.start_date} → {selectedCancelRequest.end_date}</p>
                  <p className="text-xs text-orange-700 mt-2">Employee reason: {selectedCancelRequest.cancellation_reason}</p>
                </div>
                <div>
                  <Label>Reason for rejecting cancellation *</Label>
                  <Textarea value={cancelRejectReason} onChange={(e) => setCancelRejectReason(e.target.value)} rows={3} placeholder="Why is cancellation not allowed?" />
                </div>
                <p className="text-xs text-md3-on-surface-variant">If you reject, the leave will remain in <strong>approved</strong> status.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCancelRejectDialog(false)}>Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectCancellation} data-testid="cancel-reject-confirm-btn">Reject Cancellation</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* HR Force Cancel Dialog */}
        <Dialog open={forceCancelDialog} onOpenChange={setForceCancelDialog}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-purple-700">HR Force Cancel</DialogTitle>
            </DialogHeader>
            {forceCancelTarget && (
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 p-3 rounded text-sm">
                  <p className="font-semibold">{forceCancelTarget.employee_name}</p>
                  <p>{forceCancelTarget.leave_type} · {forceCancelTarget.total_days} days · {forceCancelTarget.start_date} → {forceCancelTarget.end_date}</p>
                  <p className="text-xs mt-1">Force-cancel skips the approval flow. Balance will be restored immediately.</p>
                </div>
                <div>
                  <Label>HR Reason *</Label>
                  <Textarea value={forceCancelReason} onChange={(e) => setForceCancelReason(e.target.value)} rows={3} placeholder="Why is HR force-cancelling?" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setForceCancelDialog(false)}>Cancel</Button>
                  <Button className="bg-purple-700 hover:bg-purple-800 text-white" onClick={submitForceCancel} data-testid="force-cancel-confirm-btn">Force Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Encashment Reject Dialog */}
        <Dialog open={encashRejectDialog} onOpenChange={setEncashRejectDialog}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small">Reject Encashment Request</DialogTitle>
            </DialogHeader>
            {selectedEncashRequest && (
              <div className="space-y-4">
                <div className="bg-md3-surface p-4 rounded-md3-lg space-y-1">
                  <p className="font-semibold">{selectedEncashRequest.employee_name}</p>
                  <p className="text-sm">{selectedEncashRequest.days} days · {selectedEncashRequest.reason}</p>
                </div>
                <div>
                  <Label>Rejection Reason *</Label>
                  <Textarea value={encashRejectReason} onChange={(e) => setEncashRejectReason(e.target.value)} rows={3} placeholder="Explain why" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setEncashRejectDialog(false)}>Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectEncashment} data-testid="encash-reject-confirm-btn">Reject</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Reject Leave Application</DialogTitle>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-6">
                <div className="bg-md3-surface p-4 rounded-md3-lg space-y-2">
                  <p className="font-semibold">{selectedApplication.employee_name}</p>
                  <p className="text-md3-body-small">
                    {selectedApplication.leave_type.charAt(0).toUpperCase() + selectedApplication.leave_type.slice(1)} • {selectedApplication.total_days} days
                  </p>
                  <p className="text-md3-body-small">{selectedApplication.start_date} to {selectedApplication.end_date}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-md3-label-large text-gray-700 font-medium">Reason for Rejection *</Label>
                  <Textarea className="bg-white border-gray-300" value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a clear reason for rejection" rows={4} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectionReason(""); setSelectedApplication(null); }}>
                    Cancel
                  </Button>
                  <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleReject} disabled={processing || !rejectionReason.trim()}>
                    {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : "Confirm Rejection"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Mark as LOP Dialog */}
        <Dialog open={lopDialogOpen} onOpenChange={setLopDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small font-semibold text-red-700">Mark as Loss of Pay</DialogTitle>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded">
                  <p className="font-semibold">{selectedApplication.employee_name}</p>
                  <p className="text-sm">{selectedApplication.leave_type} • {selectedApplication.total_days} days</p>
                  <p className="text-sm">{selectedApplication.start_date} to {selectedApplication.end_date}</p>
                </div>
                <p className="text-sm text-gray-600">This will mark the leave as Loss of Pay. If previously approved, the leave balance will be restored.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setLopDialogOpen(false); setSelectedApplication(null); }}>Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleMarkLop} disabled={processing} data-testid="confirm-lop-btn">
                    {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : "Confirm LOP"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Direct LOP Dialog (HR only) */}
        <Dialog open={directLopDialogOpen} onOpenChange={setDirectLopDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small font-semibold">Record Direct LOP</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleDirectLop} className="space-y-4">
              <p className="text-sm text-gray-500">Use this for unauthorized absence, failure to apply, or any other direct LOP case.</p>
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select value={directLopData.employee_id} onValueChange={(v) => setDirectLopData({ ...directLopData, employee_id: v })}>
                  <SelectTrigger className="bg-white border-gray-300" data-testid="direct-lop-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.employee_id}>
                        {emp.employee_id} - {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="lop_half_day" checked={directLopData.is_half_day}
                  onChange={(e) => setDirectLopData({ ...directLopData, is_half_day: e.target.checked })} className="h-4 w-4" />
                <Label htmlFor="lop_half_day" className="cursor-pointer">Half Day</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input type="date" className="bg-white border-gray-300" value={directLopData.start_date}
                    onChange={(e) => setDirectLopData({ ...directLopData, start_date: e.target.value, end_date: directLopData.is_half_day ? e.target.value : directLopData.end_date })} required />
                </div>
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input type="date" className="bg-white border-gray-300" value={directLopData.is_half_day ? directLopData.start_date : directLopData.end_date}
                    onChange={(e) => setDirectLopData({ ...directLopData, end_date: e.target.value })} disabled={directLopData.is_half_day} required={!directLopData.is_half_day} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Textarea className="bg-white border-gray-300" value={directLopData.reason}
                  onChange={(e) => setDirectLopData({ ...directLopData, reason: e.target.value })}
                  placeholder="Reason for LOP (e.g., unauthorized absence, failure to apply)" rows={3} required />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDirectLopDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={processing || !directLopData.employee_id}>
                  {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : "Create LOP Record"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Planning Impact Dialog — shows tasks of the applicant overlapping the leave window */}
        <Dialog
          open={planningImpactDialog.open}
          onOpenChange={(open) => setPlanningImpactDialog((d) => ({ ...d, open }))}
        >
          <DialogContent className="max-w-2xl bg-md3-surface-container-high rounded-md3-xl" data-testid="planning-impact-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-md3-title-large">
                <ClipboardList className="h-5 w-5 text-md3-primary" />
                Planning Impact
              </DialogTitle>
            </DialogHeader>
            {planningImpactDialog.application && (
              <div className="text-sm space-y-1 mb-2">
                <div>
                  <span className="text-md3-on-surface-variant">Applicant:</span>{" "}
                  <span className="font-medium">{planningImpactDialog.application.employee_name}</span>
                </div>
                <div>
                  <span className="text-md3-on-surface-variant">Leave window:</span>{" "}
                  <span className="font-medium">
                    {planningImpactDialog.application.start_date} → {planningImpactDialog.application.end_date}
                  </span>
                  {planningImpactDialog.application.is_half_day && (
                    <Badge variant="outline" className="ml-2 text-xs">Half day</Badge>
                  )}
                </div>
              </div>
            )}
            {planningImpactDialog.loading ? (
              <div className="py-6 text-center text-sm text-md3-on-surface-variant">
                <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading planning data…
              </div>
            ) : planningImpactDialog.data ? (
              <div className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                    {planningImpactDialog.data.summary?.overlap_task_count || 0} overlapping
                    {" · "}
                    {planningImpactDialog.data.summary?.overlap_hours || 0}h
                  </Badge>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                    {planningImpactDialog.data.summary?.follow_up_task_count || 0} due within {planningImpactDialog.data.follow_up_days || 3}d after leave
                    {" · "}
                    {planningImpactDialog.data.summary?.follow_up_hours || 0}h
                  </Badge>
                </div>

                {/* Overlapping tasks */}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant mb-1">
                    Tasks Overlapping Leave
                  </div>
                  {(planningImpactDialog.data.overlapping || []).length === 0 ? (
                    <p className="text-xs text-md3-on-surface-variant">No overlapping tasks — safe to approve.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase text-md3-on-surface-variant border-b">
                            <th className="py-1 pr-2">Project</th>
                            <th className="py-1 pr-2">Task</th>
                            <th className="py-1 pr-2">Dates</th>
                            <th className="py-1 pr-2 text-right">Hours</th>
                            <th className="py-1">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planningImpactDialog.data.overlapping.map((r) => (
                            <tr key={r.task_id} className="border-b last:border-0">
                              <td className="py-1.5 pr-2 font-mono text-md3-primary">{r.project_number}</td>
                              <td className="py-1.5 pr-2">{r.description || "—"}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">{r.planned_start_date || "—"} → {r.due_date || "—"}</td>
                              <td className="py-1.5 pr-2 text-right">{r.allocated_hours}</td>
                              <td className="py-1.5">
                                <Badge variant="outline" className="text-[10px]">{r.workflow_state || r.status || "—"}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Follow-up tasks */}
                {(planningImpactDialog.data.follow_up || []).length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-md3-on-surface-variant mb-1">
                      Due within {planningImpactDialog.data.follow_up_days}d after Return
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase text-md3-on-surface-variant border-b">
                            <th className="py-1 pr-2">Project</th>
                            <th className="py-1 pr-2">Task</th>
                            <th className="py-1 pr-2">Dates</th>
                            <th className="py-1 pr-2 text-right">Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planningImpactDialog.data.follow_up.map((r) => (
                            <tr key={r.task_id} className="border-b last:border-0">
                              <td className="py-1.5 pr-2 font-mono text-md3-primary">{r.project_number}</td>
                              <td className="py-1.5 pr-2">{r.description || "—"}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">{r.planned_start_date || "—"} → {r.due_date || "—"}</td>
                              <td className="py-1.5 pr-2 text-right">{r.allocated_hours}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-md3-on-surface-variant">No data.</p>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setPlanningImpactDialog({ open: false, loading: false, application: null, data: null })}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
